import { PublicKey } from '@solana/web3.js';
import { ethers } from 'ethers';
import { validate } from 'multicoin-address-validator';
import { bech32 } from 'bech32';

export function validateCardanoAddress(address: string): boolean {
  return validate(address, 'Cardano');
}

export function validateRippleAddress(address: string): boolean {
  return validate(address, 'Ripple');
}

export function validateTronAddress(address: string): boolean {
  return validate(address, 'Tron');
}

export function validateEthereumAddress(address: string): boolean {
  return ethers.utils.isAddress(address);
}

export function validateStarknetAddress(address: string): boolean {
  const starknetAddressRegex = /^0x[a-fA-F0-9]{64}$/;
  return starknetAddressRegex.test(address);
}

export function validateStellarAddress(address: string): boolean {
  const stellarAddressRegex = /^(G[A-Z2-7]{55}|M[A-Z2-7]{68})$/;
  return stellarAddressRegex.test(address);
}

export function validateSolanaAddress(address: string): boolean {
  try {
    new PublicKey(address);
    return true;
  } catch (error) {
    return false;
  }
}

export function validateCosmosAddress(address: string): boolean {
  try {
    const { prefix: decodedPrefix } = bech32.decode(address);

    if (!['cosmos', 'osmos', 'osmo'].includes(decodedPrefix)) {
      return false;
    }
  } catch (error) {
    return false;
  }

  return true;
}

export function validateDydxAddress(address: string): boolean {
  try {
    const { prefix: decodedPrefix } = bech32.decode(address);

    if (decodedPrefix !== 'dydx') {
      return false;
    }
  } catch (error) {
    return false;
  }

  return true;
}

export function validateNeutronAddress(address: string): boolean {
  try {
    const { prefix: decodedPrefix } = bech32.decode(address);

    if (decodedPrefix !== 'neutron') {
      return false;
    }
  } catch (error) {
    return false;
  }

  return true;
}

export function validateSeiAddress(address: string): boolean {
  try {
    const { prefix: decodedPrefix } = bech32.decode(address);

    if (decodedPrefix !== 'sei') {
      return false;
    }
  } catch (error) {
    return false;
  }

  return true;
}

export function validateNobleAddress(address: string): boolean {
  try {
    const { prefix: decodedPrefix } = bech32.decode(address);

    if (decodedPrefix !== 'noble') {
      return false;
    }
  } catch (error) {
    return false;
  }

  return true;
}

export function validateArweaveAddress(address: string): boolean {
  // Check for known blockchain prefixes
  const commonPrefixes = [
    'dydx1',
    'cosmos1',
    'terra1',
    'osmo1',
    'juno1',
    'akash1',
    'kava1',
    'bc1',
    'ltc1',
    'bnb1',
    'one1',
    'zil1',
    'erd1',
    'thor1',
    'maya1',
    'atom1',
    'band1',
    'cro1',
    'iaa1',
    'ixo1',
    'sent1',
    'like1',
    'desmos1',
    'persistence1',
    'fetch1',
    'ki1',
    'cheq1',
    'stars1',
    'huahua1',
    'chihuahua1',
    '0x',
    'addr1',
    'stake1',
    'tz1',
    'tz2',
    'tz3',
    'KT1',
    'near',
    'sol',
    'polkadot',
    'kusama',
    'nano_',
    'xrb_',
    'zen1',
    'zs1',
    'hx',
    'cx',
    'account_rdx',
    'oasis1',
    'secret1',
    'archway1',
    'neutron1',
    'strd1',
    'sei1',
    'noble1',
  ];

  for (const prefix of commonPrefixes) {
    if (address.toLowerCase().startsWith(prefix.toLowerCase())) {
      return false;
    }
  }

  // Check if it's likely a Solana address (base58 encoded, 32-44 chars)
  // Solana uses base58 which excludes: 0, O, I, l
  // and never contains - or _
  const isSolanaAddress = validateSolanaAddress(address);
  if (isSolanaAddress) {
    return false;
  }

  // Arweave addresses are exactly 43 characters and can contain - and _
  // They use URL-safe base64 encoding
  return address.match(/^[a-zA-Z0-9-_]{43}$/) !== null;
}

export function validateAvalancheAddress(address: string): boolean {
  return validate(address, 'Avalanche');
}

export function validateSuiAddress(address: string): boolean {
  // SUI addresses are 32-byte addresses that start with "0x" followed by 64 hexadecimal characters
  const suiAddressRegex = /^0x[a-fA-F0-9]{64}$/;
  return suiAddressRegex.test(address);
}

export function validateFlowAddress(address: string): boolean {
  //A Flow address is represented as 16 hex-encoded characters (usually prefixed with 0x to indicate hex encoding).
  const flowAddressRegex = /^(0x|)[a-fA-F0-9]{16}$/;
  return flowAddressRegex.test(address);
}

export function validateShapeAddress(address: string): boolean {
  return validateEthereumAddress(address);
}

export const validateAddress = (address: string) => {
  return [
    validateEthereumAddress,
    validateSolanaAddress,
    validateCardanoAddress,
    validateRippleAddress,
    validateArweaveAddress,
    validateCosmosAddress,
    validateDydxAddress,
    validateNeutronAddress,
    validateAvalancheAddress,
    validateTronAddress,
    validateStarknetAddress,
    validateSuiAddress,
    validateSeiAddress,
    validateNobleAddress,
    validateFlowAddress,
    validateShapeAddress,
  ].some((validator) => validator(address));
};
